import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Settings, CreditCard, X, ArrowRight, CheckCircle2 } from 'lucide-react';

const ONBOARDING_KEY = 'store_onboarding_completed';

export function StoreOnboarding() {
  const navigate = useNavigate();
  const [show, setShow] = useState(false);
  const [step, setStep] = useState(1);
  const [highlightSettings, setHighlightSettings] = useState(false);
  const [highlightPayments, setHighlightPayments] = useState(false);

  useEffect(() => {
    // Verificar si el usuario ya completó el onboarding
    const completed = localStorage.getItem(ONBOARDING_KEY);
    
    // Mostrar solo si no ha completado el onboarding
    if (!completed) {
      // Pequeño delay para que cargue la interfaz primero
      setTimeout(() => {
        setShow(true);
      }, 1000);
    }
  }, []);

  const handleNext = () => {
    if (step === 1) {
      // Resaltar botón de Ajustes
      setHighlightSettings(true);
      setStep(2);
    } else if (step === 2) {
      // Quitar resaltado de Ajustes, resaltar Pagos
      setHighlightSettings(false);
      setHighlightPayments(true);
      setStep(3);
    } else if (step === 3) {
      // Finalizar y navegar a Ajustes
      completeOnboarding();
      navigate('/panel/ajustes');
    }
  };

  const completeOnboarding = () => {
    localStorage.setItem(ONBOARDING_KEY, 'true');
    setShow(false);
    setHighlightSettings(false);
    setHighlightPayments(false);
  };

  const handleSkip = () => {
    completeOnboarding();
  };

  if (!show) return null;

  return (
    <>
      {/* Overlay oscuro */}
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100]" />

      {/* Modal central */}
      <div className="fixed inset-0 z-[101] flex items-center justify-center p-4">
        <div className="relative bg-gradient-to-br from-[#0a0a0a] to-[#1a1a1a] border border-white/10 rounded-2xl shadow-2xl max-w-lg w-full p-8">
          
          {/* Botón cerrar */}
          <button
            onClick={handleSkip}
            className="absolute top-4 right-4 text-white/40 hover:text-white transition-colors"
          >
            <X className="h-5 w-5" />
          </button>

          {/* Contenido según el paso */}
          {step === 1 && (
            <div className="space-y-6">
              <div className="flex items-center justify-center w-16 h-16 mx-auto bg-gradient-to-br from-cyan-500 to-blue-600 rounded-2xl">
                <CheckCircle2 className="h-8 w-8 text-white" />
              </div>
              
              <div className="text-center space-y-3">
                <h2 className="text-2xl font-bold text-white">
                  ¡Bienvenido a TiendasYA! 🎉
                </h2>
                <p className="text-white/60 text-sm leading-relaxed">
                  Antes de empezar a vender, necesitas configurar algunos parámetros importantes de tu tienda.
                </p>
              </div>

              <div className="bg-white/5 border border-white/10 rounded-xl p-4 space-y-2">
                <p className="text-white/80 text-sm font-medium">
                  Te guiaremos en 2 pasos esenciales:
                </p>
                <ul className="space-y-2 text-white/60 text-sm">
                  <li className="flex items-start gap-2">
                    <Settings className="h-4 w-4 mt-0.5 text-cyan-400 flex-shrink-0" />
                    <span><strong className="text-white">Ajustes:</strong> Configura tu tienda, horarios, envíos y más</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CreditCard className="h-4 w-4 mt-0.5 text-green-400 flex-shrink-0" />
                    <span><strong className="text-white">Pagos:</strong> Agrega tus métodos de pago para recibir dinero</span>
                  </li>
                </ul>
              </div>

              <button
                onClick={handleNext}
                className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-bold py-3 px-6 rounded-xl hover:shadow-[0_0_20px_rgba(6,182,212,0.5)] transition-all"
              >
                Comenzar
                <ArrowRight className="h-4 w-4" />
              </button>

              <button
                onClick={handleSkip}
                className="w-full text-white/40 hover:text-white text-sm transition-colors"
              >
                Omitir tutorial
              </button>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6">
              <div className="flex items-center justify-center w-16 h-16 mx-auto bg-gradient-to-br from-cyan-500 to-blue-600 rounded-2xl">
                <Settings className="h-8 w-8 text-white" />
              </div>
              
              <div className="text-center space-y-3">
                <h2 className="text-2xl font-bold text-white">
                  Paso 1: Ajustes de tu Tienda
                </h2>
                <p className="text-white/60 text-sm leading-relaxed">
                  En <strong className="text-cyan-400">Ajustes</strong> encontrarás todo lo necesario para configurar tu tienda:
                </p>
              </div>

              <div className="bg-white/5 border border-white/10 rounded-xl p-4 space-y-3">
                <ul className="space-y-2 text-white/70 text-sm">
                  <li className="flex items-start gap-2">
                    <span className="text-cyan-400">•</span>
                    <span>Información básica (nombre, descripción, logo)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-cyan-400">•</span>
                    <span>Horarios de atención</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-cyan-400">•</span>
                    <span>Métodos de envío y retiro</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-cyan-400">•</span>
                    <span>Políticas de devolución y cancelación</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-cyan-400">•</span>
                    <span>Redes sociales y contacto</span>
                  </li>
                </ul>
              </div>

              <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-xl p-4">
                <p className="text-cyan-400 text-sm font-medium text-center">
                  👈 Mira el menú lateral, el botón "Ajustes" está resaltado
                </p>
              </div>

              <button
                onClick={handleNext}
                className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-bold py-3 px-6 rounded-xl hover:shadow-[0_0_20px_rgba(6,182,212,0.5)] transition-all"
              >
                Siguiente
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-6">
              <div className="flex items-center justify-center w-16 h-16 mx-auto bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl">
                <CreditCard className="h-8 w-8 text-white" />
              </div>
              
              <div className="text-center space-y-3">
                <h2 className="text-2xl font-bold text-white">
                  Paso 2: Métodos de Pago
                </h2>
                <p className="text-white/60 text-sm leading-relaxed">
                  En <strong className="text-green-400">Pagos</strong> debes configurar cómo recibirás el dinero de tus ventas:
                </p>
              </div>

              <div className="bg-white/5 border border-white/10 rounded-xl p-4 space-y-3">
                <ul className="space-y-2 text-white/70 text-sm">
                  <li className="flex items-start gap-2">
                    <span className="text-green-400">•</span>
                    <span>Transferencias bancarias</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-400">•</span>
                    <span>Pago móvil</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-400">•</span>
                    <span>Efectivo</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-400">•</span>
                    <span>Otros métodos personalizados</span>
                  </li>
                </ul>
              </div>

              <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
                <p className="text-red-400 text-sm font-medium text-center">
                  ⚠️ Sin métodos de pago configurados, tus clientes no podrán completar sus compras
                </p>
              </div>

              <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4">
                <p className="text-green-400 text-sm font-medium text-center">
                  👈 El botón "Pagos" también está resaltado en el menú
                </p>
              </div>

              <button
                onClick={handleNext}
                className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white font-bold py-3 px-6 rounded-xl hover:shadow-[0_0_20px_rgba(34,197,94,0.5)] transition-all"
              >
                Ir a Ajustes
                <ArrowRight className="h-4 w-4" />
              </button>

              <button
                onClick={handleSkip}
                className="w-full text-white/40 hover:text-white text-sm transition-colors"
              >
                Lo haré después
              </button>
            </div>
          )}

          {/* Indicador de pasos */}
          <div className="flex items-center justify-center gap-2 mt-6">
            {[1, 2, 3].map((s) => (
              <div
                key={s}
                className={`h-1.5 rounded-full transition-all ${
                  s === step 
                    ? 'w-8 bg-cyan-400' 
                    : s < step 
                    ? 'w-1.5 bg-green-400' 
                    : 'w-1.5 bg-white/20'
                }`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Resaltado de botones en el sidebar */}
      {highlightSettings && (
        <style>{`
          a[href="/panel/ajustes"] {
            animation: pulse-glow 2s ease-in-out infinite;
            box-shadow: 0 0 30px rgba(6, 182, 212, 0.8) !important;
            transform: scale(1.05) !important;
          }
          @keyframes pulse-glow {
            0%, 100% { box-shadow: 0 0 20px rgba(6, 182, 212, 0.6); }
            50% { box-shadow: 0 0 40px rgba(6, 182, 212, 1); }
          }
        `}</style>
      )}

      {highlightPayments && (
        <style>{`
          a[href="/panel/pagos"] {
            animation: pulse-glow-green 2s ease-in-out infinite;
            box-shadow: 0 0 30px rgba(34, 197, 94, 0.8) !important;
            transform: scale(1.05) !important;
          }
          @keyframes pulse-glow-green {
            0%, 100% { box-shadow: 0 0 20px rgba(34, 197, 94, 0.6); }
            50% { box-shadow: 0 0 40px rgba(34, 197, 94, 1); }
          }
        `}</style>
      )}
    </>
  );
}
